/**
 * MessageRenderer
 * Encapsulates message rendering pipeline for both streaming and programmatic messages
 *
 * PURPOSE:
 * - Unified rendering pipeline for all assistant messages
 * - Handles both instant (programmatic) and streaming (LLM) text
 * - Manages lifecycle: start → process → finish
 * - Encapsulates BlockStreamBuffer and finalization logic
 *
 * USAGE:
 * - Programmatic: new MessageRenderer(chatManager).start().addText(text).finish()
 * - Streaming: renderer.start(); stream.on('chunk', chunk => renderer.addText(chunk)); stream.on('end', () => renderer.finish())
 *
 * @module MessageRenderer
 */

import { BlockStreamBuffer } from './BlockStreamBuffer.js';
import { toCopyMarkdown } from './structuredBlockRenderers.js';

export class MessageRenderer {
  /**
   * @param {ChatManager} chatManager - Parent ChatManager instance
   * @param {Object} options - Rendering options
   * @param {string} options.messageType - Message type for behavior control (e.g., 'system', 'welcome')
   * @param {number} options.staggerDelay - Delay between block renders in ms (default: 0)
   * @param {string} options.customClass - Custom CSS class for the message
   * @param {Function} [options.onBlockRender] - Fires on EVERY rendered block or
   *   list item (not once per message) — the thinking indicator's waiting→bars cue
   */
  constructor(chatManager, options = {}) {
    this.chatManager = chatManager;
    this.options = options;

    // Pipeline components (initialized on start())
    this.messageElement = null;
    this.messageContainer = null;
    this.streamBuffer = null;

    // State tracking
    this.isStarted = false;
    this.isFinished = false;

    // Accumulated markdown for copy functionality
    this.accumulatedMarkdown = '';
  }

  /**
   * Initialize the rendering pipeline
   * Creates message element and sets up processing components
   * @returns {MessageRenderer} this (for chaining)
   */
  start() {
    if (this.isStarted) {
      throw new Error('MessageRenderer already started');
    }

    // Create message element with streaming indicator and optional messageType
    this.messageElement = this.chatManager.messageList.addAssistantMessage('', {
      streaming: true,
      messageType: this.options.messageType,
      customClass: this.options.customClass
    });

    // Get message content container
    this.messageContainer = this.messageElement.querySelector('.message__content');
    if (!this.messageContainer) {
      throw new Error('Message content container not found');
    }

    // Initialize block stream buffer with standard config
    // Pass staggerDelay if provided (for programmatic messages with sequential block rendering)
    this.streamBuffer = new BlockStreamBuffer(this.messageContainer, {
      fadeInDuration: 200,
      fadeInEasing: 'ease-out',
      slideDistance: 6,
      staggerDelay: this.options.staggerDelay || 0,
      ...(this.options.minBlockSpacing !== undefined ? { minBlockSpacing: this.options.minBlockSpacing } : {}),
      onBlockRender: this.options.onBlockRender || null
    });

    this.isStarted = true;
    return this;
  }

  /**
   * Process a text chunk through the pipeline
   * Can be called multiple times for streaming, or once for programmatic messages
   * @param {string} text - Text chunk to process
   * @returns {MessageRenderer} this (for chaining)
   */
  addText(text) {
    if (!this.isStarted) {
      throw new Error('MessageRenderer not started. Call start() first.');
    }
    if (this.isFinished) {
      throw new Error('MessageRenderer already finished. Cannot add more text.');
    }

    // Accumulate raw markdown for copy functionality
    this.accumulatedMarkdown += text;

    // Pass text directly to orchestrator
    this.streamBuffer.addChunk(text);

    return this;
  }

  /**
   * Finalize the rendering pipeline
   * Flushes buffers, executes queued actions, removes streaming indicator
   * @param {{suppressActionBar?: boolean}} [opts] - `suppressActionBar: true`
   *   when the caller is about to discard or replace the bubble (e.g. the error
   *   banner path), so no action bar is built on a detached element.
   * @returns {HTMLElement} The finalized message element
   */
  finish({ suppressActionBar = false } = {}) {
    if (!this.isStarted) {
      throw new Error('MessageRenderer not started. Call start() first.');
    }
    if (this.isFinished) {
      // Allow idempotent finish() calls - just return message element
      return this.messageElement;
    }

    try {
      // Finalize orchestrator (flush text buffer)
      this.streamBuffer.finalize();

      // Remove streaming class immediately (but don't add action bar yet)
      this.messageElement.classList.remove('message--streaming');

      // Convert structured block tags to readable markdown for copy. Shared with
      // the poll/replay path so copy text can't drift between surfaces.
      if (this.accumulatedMarkdown) {
        this.accumulatedMarkdown = toCopyMarkdown(this.accumulatedMarkdown);
        this.messageElement.dataset.markdown = this.accumulatedMarkdown;
      }
    } finally {
      const deactivationPromise = this.chatManager.deactivateThinkingIndicator();

      // Add action bar after thinking indicator animation completes
      // This prevents layout shift that would cause the thinking indicator to jump down
      const messageElement = this.messageElement;
      const messageList = this.chatManager.messageList;

      deactivationPromise.then(() => {
        if (suppressActionBar) return;
        // The bubble may have been replaced (error banner) or otherwise removed
        // while the indicator was tearing down — never build a bar, or register
        // tooltips, on a detached element.
        if (!messageElement.isConnected) return;
        if (messageElement.classList.contains('message--assistant')) {
          messageList._addActionBar(messageElement);
        }
      });
    }

    this.isFinished = true;
    return this.messageElement;
  }

  /**
   * Get the current message element
   * @returns {HTMLElement|null} Message element (null if not started)
   */
  getMessageElement() {
    return this.messageElement;
  }

  /**
   * Get the total animation duration for staggered blocks
   * Useful for knowing when to show components after text animation
   * @returns {number} Total duration in ms (0 if not started or no stagger)
   */
  getTotalAnimationDuration() {
    if (!this.streamBuffer) return 0;
    return this.streamBuffer.getTotalAnimationDuration();
  }

  /**
   * Whether this message rendered a self-timed structured block (e.g. the
   * memory-update card) that keeps animating past its own fade-in.
   * @returns {boolean}
   */
  hasSelfTimedHold() {
    if (!this.streamBuffer) return false;
    return this.streamBuffer.hasSelfTimedHold();
  }

  /**
   * Get current renderer state (for debugging/testing)
   * @returns {Object} State object
   */
  getState() {
    return {
      isStarted: this.isStarted,
      isFinished: this.isFinished,
      hasMessageElement: !!this.messageElement,
      hasStreamBuffer: !!this.streamBuffer
    };
  }

}
